import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { selectIsLoading } from 'features/applications/applicationsSelectors';
import { selectTourState } from 'features/tour/tourSelectors';

import { useStartTour } from 'hooks/tourHooks';
import { useAppSelector } from 'hooks/useTypedRedux';

import { scrollToTop } from 'utils/scrollToTop';

import { GuideType } from 'constants/enums';
import { HOW_IT_WORKS_TOUR_DELAY_MS } from 'constants/tour';

import { ReactComponent as MessageQuestionIcon } from 'assets/icons/message-question.svg';

import styles from './HowItWorks.module.scss';
import HowItWorksModal from './HowItWorksModal/HowItWorksModal';
import HowItWorksNoDataModal from './HowItWorksNoDataModal/HowItWorksNoDataModal';

const HowItWorks = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const startTour = useStartTour();
  const tourState = useAppSelector(selectTourState);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNoDataModalOpen, setIsNoDataModalOpen] = useState(false);
  const [noDataModalType, setNoDataModalType] = useState<GuideType | null>(
    null
  );
  const isApplicationsLoading = useSelector(selectIsLoading);
  const isJobsLoading = useSelector(selectIsLoading);
  const pageSteps = tourState.steps;

  const handleOpenModal = () => {
    return;
    // setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenNoDataModal = () => {
    setIsNoDataModalOpen(true);
  };

  const handleCloseNoDataModal = () => {
    setIsNoDataModalOpen(false);
  };

  useEffect(() => {
    if (isApplicationsLoading || isJobsLoading) {
      return;
    }

    const isApplicationTourOpen = tourState.isOpenApplicationTour;
    const isJobTourOpen = tourState.isOpenJobTour;

    if (!isApplicationTourOpen && !isJobTourOpen) {
      return;
    }

    const showNoDataModal = (modalType: GuideType) => {
      setNoDataModalType(modalType);
      handleOpenNoDataModal();
    };

    if (isApplicationTourOpen && !tourState.applicationId) {
      showNoDataModal(GuideType.Applications);

      return;
    }

    if (isJobTourOpen && !tourState.jobId) {
      showNoDataModal(GuideType.Jobs);
      return;
    }

    scrollToTop();
    const timer = setTimeout(() => {
      startTour(pageSteps ?? [], pathname, false);
    }, HOW_IT_WORKS_TOUR_DELAY_MS);

    return () => clearTimeout(timer);
  }, [
    tourState.isOpenJobTour,
    tourState.jobId,
    tourState.isOpenApplicationTour,
    tourState.applicationId,
    pageSteps,
    pathname,
    isApplicationsLoading,
    isJobsLoading,
    startTour
  ]);

  return (
    <>
      <div className={styles.container} onClick={handleOpenModal}>
        <div className={styles.iconWrapper}>
          <MessageQuestionIcon />
        </div>

        <div className={styles.textWrapper}>{t('tour.button.howItWorks')}</div>
      </div>

      <HowItWorksModal isOpen={isModalOpen} onClose={handleCloseModal} />

      {noDataModalType && (
        <HowItWorksNoDataModal
          type={noDataModalType}
          isOpen={isNoDataModalOpen}
          onClose={handleCloseNoDataModal}
        />
      )}
    </>
  );
};

export default HowItWorks;
